import { useCallback, useRef, useState } from "react";
import type { ClipboardEvent, RefObject } from "react";
import { clipboardFiles, readClipboardImage } from "@/lib/clipboard-image";
import { readNativeClipboardFiles } from "@/lib/native-clipboard-files";
import { isTauri } from "@/lib/is-tauri";
import { translate } from "@/i18n";
import { inputBarMessages } from "@/i18n/messages/input-bar";
import { useNotificationStore } from "@/stores/notification-store";
import type { ComposerFieldHandle } from "./standalone-bar-field";

type UseInputBarPasteArgs = {
  autoResize: (element: ComposerFieldHandle) => void;
  inputRef: RefObject<ComposerFieldHandle | null>;
  onChange: (value: string) => void;
  onImageFile?: (
    file: File,
    source: "paste" | "drop",
    position?: { x: number; y: number },
  ) => void;
  disabled?: boolean;
};

export function useInputBarPaste({
  autoResize,
  inputRef,
  onChange,
  onImageFile,
  disabled = false,
}: UseInputBarPasteArgs) {
  const [pending, setPending] = useState(0);
  const latest = useRef({ autoResize, onChange, onImageFile, disabled });
  latest.current = { autoResize, onChange, onImageFile, disabled };

  const insertText = useCallback(
    (text: string) => {
      const el = inputRef.current;
      if (!el || !text || latest.current.disabled) return;
      // Read the current draft: image conversion may finish after another edit.
      const value = el.value;
      const start = el.selectionStart ?? value.length;
      const end = el.selectionEnd ?? start;
      const next = value.slice(0, start) + text + value.slice(end);
      const caret = start + text.length;
      // Set the caret before React reads the draft for / and @ popups.
      el.value = next;
      el.setSelectionRange(caret, caret);
      latest.current.onChange(next);
      el.focus();
      latest.current.autoResize(el);
    },
    [inputRef],
  );

  const pasteFromSystemClipboard = useCallback(async () => {
    const el = inputRef.current;
    if (!isTauri() || !el || latest.current.disabled) return false;
    setPending((count) => count + 1);
    try {
      const { readText } = await import("@tauri-apps/plugin-clipboard-manager");
      // Snapshot both formats promptly: dictation replaces its image clipboard
      // with text shortly after issuing the paste shortcut.
      const filesPromise = latest.current.onImageFile
        ? Promise.allSettled([readNativeClipboardFiles(), readClipboardImage()])
            .then(([files, image]) => {
              if (files.status === "fulfilled" && files.value.length)
                return files.value;
              if (files.status === "rejected")
                useNotificationStore.getState().addSystemToast({
                  id: `clipboard-file-${crypto.randomUUID()}`,
                  title: translate(
                    inputBarMessages,
                    "attachClipboardFileFailed",
                  ),
                  description: String(files.reason),
                  level: "error",
                });
              if (image.status === "fulfilled" && image.value)
                return [image.value];
              if (image.status === "rejected") throw image.reason;
              return [];
            })
            .catch(() => {
              useNotificationStore.getState().addSystemToast({
                id: `clipboard-image-${crypto.randomUUID()}`,
                title: translate(
                  inputBarMessages,
                  "attachClipboardImageFailed",
                ),
                description: translate(inputBarMessages, "copyImageAgain"),
                level: "error",
              });
              return [];
            })
        : null;
      const [files, text] = await Promise.all([
        filesPromise,
        readText()
          .catch(() => null)
          .then((text) => {
            // Do not hold text behind PNG encoding: another paste can arrive
            // while that image is being prepared.
            if (inputRef.current === el && !latest.current.disabled && text)
              insertText(text);
            return text;
          }),
      ]);
      if (inputRef.current !== el || latest.current.disabled) return false;
      files?.forEach((file) => latest.current.onImageFile?.(file, "paste"));
      return Boolean(files?.length || text);
    } finally {
      setPending((count) => count - 1);
    }
  }, [inputRef, insertText]);

  const handlePaste = useCallback(
    (event: ClipboardEvent<HTMLElement>) => {
      if (latest.current.disabled) return;
      const files = latest.current.onImageFile
        ? clipboardFiles(event.clipboardData)
        : [];
      if (files.length) {
        event.preventDefault();
        event.stopPropagation();
        files.forEach((file) => latest.current.onImageFile?.(file, "paste"));
        insertText(event.clipboardData?.getData?.("text/plain") ?? "");
      } else if (isTauri()) {
        // Edit > Paste and accessibility-triggered paste can bypass keydown.
        event.preventDefault();
        event.stopPropagation();
        void pasteFromSystemClipboard();
      }
    },
    [insertText, pasteFromSystemClipboard],
  );

  return { handlePaste, pasteFromSystemClipboard, pasting: pending > 0 };
}
